/* ═══════════════════════════════════════════════════════════════
   SYNC — the forma valve (docs/SYNC_PLAN.md §3.2, engine customer 5).

   A forma's Review unit is a SETTING, never a character:
     · zone      — one zone's token order / text (header_footer.footerOddLeft,
                   special.bodyTopLeft, …), a zone's token styles, an image
     · level     — one field of one level style (levels[2].font)
     · page      — one page setting (doc_settings.marginTop)
     · special   — End of Section / blank page / post-body settings
     · placeholder — one {P#} provenance entry

   Same principles as the content engine (§0), at the setting level:
     upstream changed a setting the copy did not touch → one item
     the copy already has upstream's value            → reconciled
     both changed it differently                      → one item (upstream's
                                                        value shown, the copy's
                                                        current value plain)
   Nothing is ever auto-picked.

   Pure. No React, no I/O, no Yjs.
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig } from '../forma';
import type { Change, ChangeKind, Decision } from './types';
import { formaStringLabel, lineSpacingLabel } from './formaLabels';

export type FormaSettingKind = 'zone' | 'level' | 'page' | 'special' | 'placeholder';

/** One reviewable forma setting. `blockId` = `path`, `whole` = true. */
export interface FormaChange extends Change {
  /** `header_footer.footerOddLeft` · `levels[2].font` · `doc_settings.marginTop`
   *  · `special.endOfSectionText` · `header_footer.tokenStyles.footerOddLeft`. */
  path: string;
  settingKind: FormaSettingKind;
  /** The change in words: "Footer, left: order changed". */
  label: string;
  /** Upstream's value — what Accept writes (absent = Accept removes it). */
  value?: unknown;
  /** The value both sides started from. */
  baseValue?: unknown;
  /** Set only on a conflict: the copy's own current value (shown plain). */
  oursValue?: unknown;
}

export interface FormaReviewPlan {
  forma: StandardConfig;
  applied: number;
  rejected: number;
}

/* ─── Paths ─── */

const ROOTS = {
  doc_settings: 'docSettings',
  header_footer: 'headerFooter',
  special: 'special',
  levels: 'levels',
  placeholders: 'placeholders',
  images: 'images',
} as const;
type RootKey = keyof typeof ROOTS;

/** Never offered: the copy's own name (`— <project>` suffix) is the copy's. */
const SKIP_DOC = new Set(['standardName']);
const ZONE_RE = /^(header|footer)(First|Odd|Even)(Left|Center|Right)$/;
const BODY_ZONE_RE = /^(bodyTop|post)(Left|Center|Right)$/;

interface Setting {
  kind: FormaSettingKind;
  value: unknown;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/** Canonical JSON (sorted keys, undefined dropped) — equality of settings. */
function canon(v: unknown): string {
  if (v === undefined || v === null) return 'null';
  if (Array.isArray(v)) return '[' + v.map(canon).join(',') + ']';
  if (isObj(v)) {
    return (
      '{' +
      Object.keys(v)
        .filter((k) => v[k] !== undefined)
        .sort()
        .map((k) => JSON.stringify(k) + ':' + canon(v[k]))
        .join(',') +
      '}'
    );
  }
  return JSON.stringify(v);
}
const same = (a: unknown, b: unknown) => canon(a) === canon(b);
const absent = (v: unknown) => v === undefined || v === null;

function settingsOf(cfg: StandardConfig | null | undefined): Map<string, Setting> {
  const out = new Map<string, Setting>();
  if (!cfg) return out;
  const ds = (cfg.docSettings || {}) as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(ds)) {
    if (!SKIP_DOC.has(k)) out.set(`doc_settings.${k}`, { kind: 'page', value: v });
  }
  const zoneMap = (root: 'header_footer' | 'special', obj: Record<string, unknown>) => {
    for (const [k, v] of Object.entries(obj)) {
      if (k === 'tokenStyles' && isObj(v)) {
        for (const [z, s] of Object.entries(v)) {
          out.set(`${root}.tokenStyles.${z}`, { kind: 'zone', value: s });
        }
        continue;
      }
      const zone = root === 'header_footer' || BODY_ZONE_RE.test(k);
      out.set(`${root}.${k}`, { kind: zone ? 'zone' : 'special', value: v });
    }
  };
  zoneMap('header_footer', (cfg.headerFooter || {}) as unknown as Record<string, unknown>);
  zoneMap('special', (cfg.special || {}) as unknown as Record<string, unknown>);
  for (const lvl of cfg.levels || []) {
    if (!lvl || !Number.isFinite(lvl.level)) continue;
    for (const [k, v] of Object.entries(lvl)) {
      if (k !== 'level') out.set(`levels[${lvl.level}].${k}`, { kind: 'level', value: v });
    }
  }
  for (const [k, v] of Object.entries(cfg.placeholders || {})) {
    out.set(`placeholders.${k}`, { kind: 'placeholder', value: v });
  }
  for (const [k, v] of Object.entries(cfg.images || {})) {
    out.set(`images.${k}`, { kind: 'zone', value: v });
  }
  return out;
}

interface ParsedPath {
  root: RootKey;
  level?: number;
  keys: string[];
}

const PATH_RE = /^([a-z_]+)(?:\[(\d+)\])?(?:\.(.+))?$/;

export function parseFormaPath(path: string): ParsedPath | null {
  const m = PATH_RE.exec(path);
  if (!m || !(m[1] in ROOTS)) return null;
  const root = m[1] as RootKey;
  const rest = m[3] ?? '';
  if (root === 'levels') {
    const level = Number(m[2]);
    if (!Number.isFinite(level) || !rest) return null;
    return { root, level, keys: [rest] };
  }
  if (!rest) return null;
  // tokenStyles.<zone> is the only two-step key; image / placeholder ids are
  // taken whole.
  if ((root === 'header_footer' || root === 'special') && rest.startsWith('tokenStyles.')) {
    return { root, keys: ['tokenStyles', rest.slice('tokenStyles.'.length)] };
  }
  return { root, keys: [rest] };
}

function clone<T>(v: T): T {
  return v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T);
}

/** Deep-set one setting on a forma (mutates `cfg`). `undefined` removes it. */
export function setFormaPath(cfg: StandardConfig, path: string, value: unknown): boolean {
  const p = parseFormaPath(path);
  if (!p) return false;
  const rec = cfg as unknown as Record<string, unknown>;
  const rootKey = ROOTS[p.root];
  if (p.root === 'levels') {
    const levels = (Array.isArray(rec.levels) ? rec.levels : []) as Array<Record<string, unknown>>;
    rec.levels = levels;
    let lvl = levels.find((l) => l && l.level === p.level);
    if (!lvl) {
      if (value === undefined) return true;
      lvl = { level: p.level };
      levels.push(lvl);
      levels.sort((a, b) => Number(a.level) - Number(b.level));
    }
    if (value === undefined) delete lvl[p.keys[0]];
    else lvl[p.keys[0]] = clone(value);
    return true;
  }
  let obj = rec[rootKey] as Record<string, unknown> | undefined;
  if (!isObj(obj)) {
    if (value === undefined) return true;
    obj = {};
    rec[rootKey] = obj;
  }
  for (let i = 0; i < p.keys.length - 1; i++) {
    const k = p.keys[i];
    let next = obj[k] as Record<string, unknown> | undefined;
    if (!isObj(next)) {
      if (value === undefined) return true;
      next = {};
      obj[k] = next;
    }
    obj = next;
  }
  const last = p.keys[p.keys.length - 1];
  if (value === undefined) delete obj[last];
  else obj[last] = clone(value);
  return true;
}

/* ─── Words ─── */

const COL: Record<string, string> = { Left: 'left', Center: 'center', Right: 'right' };
const PAGE: Record<string, string> = { First: 'first page', Odd: '', Even: 'even pages' };

function zoneName(key: string): string | null {
  const m = ZONE_RE.exec(key);
  if (m) {
    const band = m[1] === 'header' ? 'Header' : 'Footer';
    const page = PAGE[m[2]];
    return `${band}${page ? ` (${page})` : ''}, ${COL[m[3]]}`;
  }
  const b = BODY_ZONE_RE.exec(key);
  if (b) return `${b[1] === 'bodyTop' ? 'Pre-body' : 'Post-body'}, ${COL[b[2]]}`;
  return null;
}

const LEVEL_FIELD: Record<string, string> = {
  font: 'font',
  fontSize: 'size',
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  allCaps: 'all caps',
  textCase: 'case',
  fontColor: 'colour',
  indent: 'indent',
  hangingIndent: 'hanging indent',
  spaceBefore: 'space before',
  spaceAfter: 'space after',
  keepWithNext: 'keep with next',
  keepLinesTogether: 'keep lines together',
  noSpaceSameStyle: 'no space between same style',
  justify: 'alignment',
};

const PAGE_FIELD: Record<string, string> = {
  marginTop: 'Top margin',
  marginBottom: 'Bottom margin',
  marginLeft: 'Left margin',
  marginRight: 'Right margin',
  pageWidth: 'Page width',
  pageHeight: 'Page height',
  bodyFont: 'Body font',
  bodyFontSize: 'Body size',
  lineSpacing: 'Line spacing',
  firstPageDifferent: 'Different first page',
  oddEvenDifferent: 'Different odd and even pages',
  widowOrphan: 'Widow and orphan control',
  specNumberFormat: 'Section number format',
  dateFormat: 'Date format',
  clientName: 'Client name',
  version: 'Forma version',
};

const SPECIAL_FIELD: Record<string, string> = {
  endOfSectionText: 'End of section text',
  eosEnabled: 'End of section',
  eosSpacing: 'End of section spacing',
  eosStyle: 'End of section style',
  blankPageEnabled: 'Blank page text',
  intentionallyBlankText: 'Blank page wording',
  blankPageCondition: 'Blank page rule',
  blankPageJustify: 'Blank page alignment',
  blankPageVertical: 'Blank page position',
  blankPageStyle: 'Blank page style',
  numberFormats: 'Numbering',
  postBodySpacing: 'Post-body spacing',
  postBodyEnabled: 'Post-body',
  zoneSpace: 'Zone spacing',
  watermark: 'Watermark',
  includeTOC: 'Table of contents',
  tocTitle: 'Table of contents title',
};

const INCH_FIELDS = new Set([
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'pageWidth',
  'pageHeight',
  'indent',
  'hangingIndent',
]);
const POINT_FIELDS = new Set([
  'fontSize',
  'bodyFontSize',
  'spaceBefore',
  'spaceAfter',
  'eosSpacing',
  'postBodySpacing',
]);

/** One token as a reader sees it: the `|style` suffix is storage detail. */
const tokenText = (t: unknown) => String(t ?? '').replace(/\|.*$/, '');

/** A setting's value in words ('' when absent). */
export function formaValueText(path: string, v: unknown): string {
  if (absent(v)) return '';
  const field = path.replace(/^.*\./, '');
  if (Array.isArray(v)) {
    return v
      .map((t) => (typeof t === 'string' ? formaStringLabel(field, t) : undefined) ?? tokenText(t))
      .filter(Boolean)
      .join(' ');
  }
  if (typeof v === 'boolean') return v ? 'On' : 'Off';
  if (typeof v === 'number') {
    if (field === 'lineSpacing') return lineSpacingLabel(v);
    if (INCH_FIELDS.has(field)) return `${+v.toFixed(3)}″`;
    if (POINT_FIELDS.has(field)) return `${+v.toFixed(2)} pt`;
    return String(+v.toFixed(3));
  }
  if (isObj(v)) {
    if (path.startsWith('images.')) return String(v.name || 'Image');
    if (path.startsWith('placeholders.')) return String(v.was ?? '');
    const parts = Object.keys(v)
      .filter((k) => !absent(v[k]))
      .sort()
      .map((k) => `${k} ${formaValueText(k, v[k])}`);
    const s = parts.join(', ');
    return s.length > 80 ? s.slice(0, 79) + '…' : s;
  }
  return typeof v === 'string' ? (formaStringLabel(field, v) ?? v) : String(v);
}

/** Doc-wide settings apply to the whole document, not one zone or level. */
export const WHOLE_DOC_NOTE = 'Applies to the whole document';

/** What the setting is called: "Footer, left" · "Level 2 font" · "Top margin". */
export function formaSettingName(path: string): string {
  const p = parseFormaPath(path);
  if (!p) return path;
  const k = p.keys[0];
  if (p.root === 'levels') return `Level ${p.level} ${LEVEL_FIELD[k] || k}`;
  if (p.root === 'doc_settings') return PAGE_FIELD[k] || k;
  if (p.root === 'placeholders') return `Placeholder ${k}`;
  if (p.root === 'images') return 'Image';
  if (k === 'tokenStyles') return `${zoneName(p.keys[1]) || p.keys[1]} token styles`;
  return zoneName(k) || SPECIAL_FIELD[k] || k;
}

function labelFor(
  path: string,
  kind: ChangeKind,
  settingKind: FormaSettingKind,
  before: string,
  after: string
): string {
  const name = formaSettingName(path);
  if (kind === 'moved') return `${name}: order changed`;
  if (kind === 'added') return `${name}: ${after ? `added ${after}` : 'added'}`;
  if (kind === 'removed') return `${name}: ${before ? `removed ${before}` : 'removed'}`;
  if (settingKind === 'zone' && !before) return `${name}: ${after}`;
  return `${name}: ${before || 'none'} → ${after || 'none'}`;
}

function sameTokensReordered(a: unknown, b: unknown): boolean {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length || same(a, b)) return false;
  const sa = a.map(canon).sort();
  const sb = b.map(canon).sort();
  return sa.every((x, i) => x === sb[i]);
}

/* ─── The engine ─── */

/**
 * Every setting upstream changed that the copy has not, one item each.
 * base = the master as the copy last took it · ours = the copy · theirs =
 * the master as just published. Stable ids (`<path>:<kind>`), sorted by path.
 */
export function merge3Forma(
  base: StandardConfig | null | undefined,
  ours: StandardConfig,
  theirs: StandardConfig
): FormaChange[] {
  const B = settingsOf(base);
  const O = settingsOf(ours);
  const T = settingsOf(theirs);
  const paths = new Set<string>([...B.keys(), ...T.keys()]);
  const out: FormaChange[] = [];
  for (const path of [...paths].sort()) {
    const b = B.get(path)?.value;
    const o = O.get(path)?.value;
    const t = T.get(path)?.value;
    if (same(b, t)) continue; // upstream did not touch it
    const settingKind = (T.get(path) || B.get(path) || O.get(path))?.kind ?? 'special';
    const conflict = !same(o, b) && !same(o, t);
    let kind: ChangeKind;
    if (same(o, t)) kind = 'reconciled';
    else if (absent(t)) kind = 'removed';
    else if (absent(b) && absent(o)) kind = 'added';
    else if (settingKind === 'zone' && sameTokensReordered(conflict ? o : b, t)) kind = 'moved';
    else kind = 'changed';
    const before = formaValueText(path, conflict ? b : (b ?? o));
    const after = formaValueText(path, t);
    const change: FormaChange = {
      id: `${path}:${kind}`,
      kind,
      blockId: path,
      whole: true,
      before,
      after,
      path,
      settingKind,
      label: labelFor(path, kind, settingKind, before, after),
    };
    if (!absent(t)) change.value = clone(t);
    if (!absent(b)) change.baseValue = clone(b);
    if (conflict && kind !== 'reconciled') {
      change.oursValue = clone(o);
      change.oursText = formaValueText(path, o);
    }
    out.push(change);
  }
  return out;
}

/** Apply the ACCEPTED settings to the copy (a new object; `ours` untouched).
 *  Rejected and undecided items leave the copy's own value. */
export function planReviewForma(
  ours: StandardConfig,
  changes: FormaChange[],
  decisions: Record<string, Decision | null | undefined>
): FormaReviewPlan {
  const forma = clone(ours);
  let applied = 0;
  let rejected = 0;
  for (const c of changes) {
    if (c.kind === 'reconciled') continue;
    const d = decisions[c.id];
    if (d === 'accept') {
      if (setFormaPath(forma, c.path, c.kind === 'removed' ? undefined : c.value)) applied++;
    } else if (d === 'reject') rejected++;
  }
  return { forma, applied, rejected };
}

/** Is this stored change a forma setting item? (rows carry plain JSON). */
export function isFormaChange(c: Change): c is FormaChange {
  return typeof (c as FormaChange).path === 'string' && !!(c as FormaChange).settingKind;
}
